/**
 * WorkItemFormDialog — J3-B (updated J3-fix-wave-4, Sheet Migration Wave 3b)
 *
 * Create sheet for work items. PRD §12.3.6 task 3 specifies a minimal form
 * (title, description, owner, domain, priority, linked Outcome, due date).
 * Parent routine is optional — domains without seeded routines are fully supported.
 *
 * UX: Items default to 'backlog' (LEAN-07 pull semantics).
 * Error surfaces: ROROS_WIP_LIMIT_REACHED (409), ROROS_DOMAIN_INACTIVE (422).
 *
 * Compliance: LEAN-07 (new items start in backlog), CT-02 (validated by service).
 * PRD §12.3.6 task 3.
 *
 * Fix-wave-4 changes:
 *   BUG-2: Parent routine is now optional (was required, deadlocking users in domains
 *          without seeded routines). Matches PRD §12.3.6 minimal form spec.
 *   BUG-3: useEffect reset uses wasOpenRef to fire only on false→true open transition.
 *          Without the ref, TanStack Query refetchOnWindowFocus creates a new domains
 *          array reference on focus return, which re-fires the effect with isOpen===true
 *          and wipes all form state the user had entered.
 *
 * Sheet Migration Wave 3b:
 *   Converted from Modal to FormSheet. Uses useFormSnapshot for all 11 form fields.
 *   The wasOpenRef pattern is preserved for resetting derived state (errors, routines)
 *   on the false→true open transition — same semantics as before, applied to non-snapshot
 *   state. Form field state is owned by useFormSnapshot which applies the same guard via
 *   its internal effect (seeds only when isOpen transitions false→true or depKey changes).
 *   Create-only (no edit mode). No URL routing.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FormSheet } from '@/components/ui/FormSheet';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select/Select';
import { DateInput } from '@/components/ui/DateInput/DateInput';
import { Alert } from '@/components/ui/Alert';
import { t } from '@/lib/i18n';
import { useRorosWorkItemCreate } from '@/hooks/roros/control-tower';
import { useRorosOutcomes } from '@/hooks/roros/artifacts';
import { useRorosTenantSettings } from '@/hooks/roros/launchpad';
import { RorosArtifactService } from '@/services/RorosService';
import { showToast } from '@/components/ui/Toast';
import type { RorosDomainDTO } from '@/types/roros/domains';
import type { RorosActorDTO } from '@/types/roros/actors';
import type { RorosRoutineArtifactDTO } from '@/types/roros/artifacts';

// ─── Types ─────────────────────────────────────────────────────────────────

interface WorkItemFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  domains: RorosDomainDTO[];
  actors: RorosActorDTO[];
  /**
   * J7-W5 task 6c-1: when set, the dialog is creating a sub-item under
   * this parent. The Parent picker is pre-filled and disabled.
   */
  defaultParentWorkItemId?: string | null;
  /**
   * J7-W5 task 6c-1: list of root work items the user can pick from.
   * Sub-items themselves are excluded by the parent (one-level rule).
   * When omitted, the picker is hidden.
   */
  parentCandidates?: { id: string; title: string }[];
}

interface FormErrors {
  title?: string;
  domain?: string;
  dueDate?: string;
  owner?: string;
  parent?: string;
  general?: string;
}

// ─── Snapshot type & diff ─────────────────────────────────────────────────────

/**
 * All 11 editable form fields as a flat snapshot for dirty-detection via FormSheet.
 * FormSheet computes isDirty = Object.values(diff(baseline, current)).some(Boolean).
 */
type WorkItemSnapshot = {
  title: string;
  description: string;
  type: string;
  domainId: string;
  ownerActorId: string;
  priority: string;
  dueDate: string;
  definitionOfDone: string;
  parentRoutineId: string;
  linkedOutcomeId: string;
  parentWorkItemId: string;
};

function diffWorkItemSnapshots(
  base: WorkItemSnapshot,
  cur: WorkItemSnapshot,
): Partial<Record<keyof WorkItemSnapshot, boolean>> {
  return {
    title: base.title !== cur.title,
    description: base.description !== cur.description,
    type: base.type !== cur.type,
    domainId: base.domainId !== cur.domainId,
    ownerActorId: base.ownerActorId !== cur.ownerActorId,
    priority: base.priority !== cur.priority,
    dueDate: base.dueDate !== cur.dueDate,
    definitionOfDone: base.definitionOfDone !== cur.definitionOfDone,
    parentRoutineId: base.parentRoutineId !== cur.parentRoutineId,
    linkedOutcomeId: base.linkedOutcomeId !== cur.linkedOutcomeId,
    parentWorkItemId: base.parentWorkItemId !== cur.parentWorkItemId,
  };
}

// ─── Constants ─────────────────────────────────────────────────────────────

const TYPE_OPTIONS = [
  { value: 'task',             label: t('Task') },
  { value: 'deliverable',      label: t('Deliverable') },
  { value: 'improvement_item', label: t('Improvement Item') },
  { value: 'intake_item',      label: t('Intake Item') },
];

const PRIORITY_OPTIONS = [
  { value: '1', label: '🔴 ' + t('Critical') },
  { value: '2', label: '🟠 ' + t('High') },
  { value: '3', label: '🟡 ' + t('Medium (default)') },
  { value: '4', label: '🟢 ' + t('Low') },
  { value: '5', label: '⚪ ' + t('Lowest') },
];

// ─── WorkItemFormDialog ────────────────────────────────────────────────────

// ─── Founder-mode due-date default (today + 7 days) ───────────────────────────
function getFounderDueDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().split('T')[0];
}

export function WorkItemFormDialog({
  isOpen,
  onClose,
  onSuccess,
  domains,
  actors,
  defaultParentWorkItemId = null,
  parentCandidates,
}: WorkItemFormDialogProps) {
  // ─── Tenant settings (for operating-mode defaults) ────────────────────────
  const tenantSettingsQuery = useRorosTenantSettings();
  const tenantSettings = tenantSettingsQuery.data as { operating_mode?: string } | undefined;
  const isFounderMode = tenantSettings?.operating_mode === 'founder';

  // In Founder mode, the org typically has a single human actor. Pre-fill with
  // the first human actor so the user doesn't have to pick the obvious choice.
  const founderActorId = useMemo(() => {
    if (!isFounderMode) return '';
    const human = actors.find((a: RorosActorDTO) => (a as any).actorType === 'human' || (a as any).actor_type === 'human');
    return human?.id ?? (actors[0]?.id ?? '');
  }, [isFounderMode, actors]);

  // ─── Form state via useFormSnapshot (Wave 3b) ────────────────────────────
  // Create-only: depKey is null so snapshot seeds only on the false→true isOpen
  // transition, not on every prop change. This is the Wave 3b equivalent of the
  // wasOpenRef pattern for form field state.
  const { state, setField, baseline, current, setBaseline } = useFormSnapshot<WorkItemSnapshot>({
    initialFactory: () => ({
      title: '',
      description: '',
      type: 'task',
      domainId: domains[0]?.id ?? '',
      // DOGFOOD-4: in Founder mode, pre-fill owner and due_date with sensible defaults.
      ownerActorId: founderActorId,
      priority: '3',
      dueDate: isFounderMode ? getFounderDueDate() : '',
      definitionOfDone: '',
      parentRoutineId: '',
      linkedOutcomeId: '',
      parentWorkItemId: defaultParentWorkItemId ?? '',
    }),
    isOpen,
    depKey: null,
  });

  // Destructure for use in JSX and submit handler.
  // Destructuring preserves the variable names expected by BUG-2 test patterns.
  const {
    title,
    description,
    type: itemType,
    domainId,
    ownerActorId,
    priority,
    dueDate,
    definitionOfDone,
    parentRoutineId,
    linkedOutcomeId,
    parentWorkItemId,
  } = state;

  // ─── Derived data ─────────────────────────────────────────────────────────
  const [routines,        setRoutines]        = useState<RorosRoutineArtifactDTO[]>([]);
  const [loadingRoutines, setLoadingRoutines] = useState(false);
  const [errors,          setErrors]          = useState<FormErrors>({});

  const createMutation = useRorosWorkItemCreate();

  // Outcomes for the selected domain — used to populate the Linked Outcome picker.
  const outcomesQuery = useRorosOutcomes(domainId && domainId !== 'none' ? domainId : '');

  // ─── Domain options ───────────────────────────────────────────────────────
  const domainOptions = useMemo(
    () => [
      { value: 'none', label: t('Select a domain…') },
      ...domains.map(d => ({ value: d.id, label: d.name })),
    ],
    [domains],
  );

  const actorOptions = useMemo(
    () => [
      { value: 'none', label: t('Select an owner…') },
      ...actors.map(a => ({ value: a.id, label: a.name })),
    ],
    [actors],
  );

  const routineOptions = useMemo(
    () => [
      { value: 'none', label: t('Select a parent routine…') },
      ...routines.map(r => ({ value: r.id, label: r.title })),
    ],
    [routines],
  );

  const outcomeOptions = useMemo(
    () => [
      { value: 'none', label: t('(none — no linked outcome)') },
      ...(outcomesQuery.data?.map(o => ({ value: o.id, label: o.title })) ?? []),
    ],
    [outcomesQuery.data],
  );

  // ─── wasOpenRef pattern — resets DERIVED state on false → true open transition.
  //
  // WHY wasOpenRef IS NECESSARY (J3-fix-wave-4 BUG-3):
  //
  // TanStack Query has `refetchOnWindowFocus: true` by default. When the user
  // switches to another window while the dialog is open and returns, the domains
  // query refetches and returns a NEW array reference (even if the data is identical).
  // Without the ref guard, having `domains` in the dep array means this effect fires
  // again with `isOpen === true` and resets every form field the user had typed.
  //
  // The ref pattern fires the reset ONLY on the false → true open transition:
  //   - wasOpenRef.current is false  →  isOpen becomes true  →  reset fires (sheet just opened)
  //   - wasOpenRef.current is true   →  isOpen is still true  →  reset does NOT fire (domains refetch)
  //   - wasOpenRef.current is true   →  isOpen becomes false  →  reset does NOT fire (sheet closing)
  //
  // Form field state is managed by useFormSnapshot which applies the same guard via
  // its own internal effect (seeds only on the false→true transition). wasOpenRef here
  // guards DERIVED / local state only: errors, routines.
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpen;

    // Only reset derived state on the false → true transition (sheet just opened).
    if (isOpen && !wasOpen) {
      setErrors({});
      setRoutines([]);
    }
  }, [isOpen]);

  // ─── Fetch routines when domain changes ───────────────────────────────────
  useEffect(() => {
    if (!domainId || domainId === 'none') {
      setRoutines([]);
      setField('parentRoutineId', '');
      setField('linkedOutcomeId', '');
      return;
    }
    // Reset outcome selection when domain changes
    setField('linkedOutcomeId', '');

    setLoadingRoutines(true);
    RorosArtifactService.listRoutineArtifacts(domainId)
      .then((data: unknown) => {
        setRoutines(Array.isArray(data) ? data : []);
        // Auto-select the first routine (Intake Scan if seeded)
        const firstRoutine = Array.isArray(data) && data.length > 0 ? data[0].id : '';
        setField('parentRoutineId', firstRoutine);
        // Update baseline too so the auto-selection does not register as a dirty change.
        setBaseline(prev => ({ ...prev, parentRoutineId: firstRoutine }));
      })
      .catch(() => setRoutines([]))
      .finally(() => setLoadingRoutines(false));
  }, [domainId, setField, setBaseline]);

  // ─── Validation ───────────────────────────────────────────────────────────
  // J3-fix-wave-4 BUG-2: parent_routine_id is optional per PRD §12.3.6.
  // Removed the "parent routine is required" validation. Users can submit without
  // selecting a parent routine (useful in domains that have no seeded routines).
  function validate(): FormErrors {
    const e: FormErrors = {};
    if (!title.trim()) e.title = t('Title is required');
    if (!domainId || domainId === 'none') e.domain = t('Domain is required');
    if (!dueDate) e.dueDate = t('Due date is required');
    if (!ownerActorId || ownerActorId === 'none') e.owner = t('Owner is required');
    return e;
  }

  // ─── Submit ───────────────────────────────────────────────────────────────
  async function handleSubmit() {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    try {
      await createMutation.mutateAsync({
        type: itemType as any,
        title: title.trim(),
        description: description.trim() || undefined,
        domainId: domainId,  // A1: camelCase on API surface
        owner_actor_id: ownerActorId,
        priority: parseInt(priority, 10),
        due_date: dueDate,
        definition_of_done: definitionOfDone.trim() || undefined,
        // parent_routine_id is optional (J3-fix-wave-4 BUG-2): send null when not selected.
        parent_routine_id: parentRoutineId && parentRoutineId !== 'none' ? parentRoutineId : null,
        outcome_id: linkedOutcomeId && linkedOutcomeId !== 'none' ? linkedOutcomeId : null,
        // J7-W5 task 6c-1: optional sub-item parent.
        parent_work_item_id: parentWorkItemId && parentWorkItemId !== 'none' ? parentWorkItemId : null,
      });
      showToast.success(t('Work item created successfully'));
      onSuccess();
    } catch (err: any) {
      const code    = err?.response?.data?.error?.code ?? err?.code ?? '';
      const message = err?.response?.data?.error?.message ?? err?.message ?? '';

      if (code === 'ROROS_WIP_LIMIT_REACHED') {
        setErrors({
          general: t(
            'WIP limit reached for this status bucket. Finish or block an in-flight item to make room, then try again.',
          ),
        });
      } else if (code === 'ROROS_DOMAIN_INACTIVE') {
        setErrors({
          domain: t('This domain is inactive. Reactivate the domain in Structure Settings before adding work items.'),
        });
      } else {
        setErrors({ general: message || t('Failed to create work item. Please try again.') });
      }
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <FormSheet<WorkItemSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={t('New Work Item')}
      mode="create"
      baseline={baseline}
      current={current}
      diff={diffWorkItemSnapshots}
      onSubmit={handleSubmit}
      isSubmitting={createMutation.isPending}
      submitLabel={t('Create Work Item')}
      submittingLabel={t('Creating…')}
      width="md"
    >
      <div className="space-y-4">
        {/* DOGFOOD-4: Founder-mode hint — auto-filled defaults */}
        {isFounderMode && (
          <Alert variant="info" title={t('Founder Mode defaults applied')}>
            {t('Defaults set for Founder mode — adjust as needed.')}
          </Alert>
        )}

        {/* General error */}
        {errors.general && (
          <Alert variant="error" title={t('Could not create work item')}>
            {errors.general}
          </Alert>
        )}

        {/* Title */}
        <Input
          label={t('Title')}
          value={title}
          onChange={e => setField('title', e.target.value)}
          placeholder={t('e.g. Write weekly digest email')}
          required
          error={errors.title}
          autoFocus
        />

        {/* Description */}
        <Textarea
          label={t('Description')}
          value={description}
          onChange={e => setField('description', e.target.value)}
          placeholder={t('Optional context or background')}
          rows={2}
        />

        {/* Type + Domain */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label={t('Type')}
            options={TYPE_OPTIONS}
            value={itemType}
            onValueChange={v => setField('type', v)}
          />
          <Select
            label={t('Domain')}
            options={domainOptions}
            value={domainId || 'none'}
            onValueChange={(v) => setField('domainId', v === 'none' ? '' : v)}
            error={errors.domain}
          />
        </div>

        {/* Linked Outcome (optional) */}
        <Select
          label={t('Linked Outcome')}
          options={outcomeOptions}
          value={linkedOutcomeId || 'none'}
          onValueChange={(v) => setField('linkedOutcomeId', v === 'none' ? '' : v)}
          disabled={!domainId || domainId === 'none' || outcomesQuery.isLoading}
          hint={
            !domainId || domainId === 'none'
              ? t('Select a domain first')
              : outcomesQuery.isLoading
              ? t('Loading outcomes…')
              : t('Optional. Link this work item to a strategic Outcome.')
          }
        />

        {/* Owner + Priority */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label={t('Owner')}
            options={actorOptions}
            value={ownerActorId || 'none'}
            onValueChange={(v) => setField('ownerActorId', v === 'none' ? '' : v)}
            error={errors.owner}
          />
          <Select
            label={t('Priority')}
            options={PRIORITY_OPTIONS}
            value={priority}
            onValueChange={v => setField('priority', v)}
          />
        </div>

        {/* Due date */}
        <DateInput
          label={t('Due Date')}
          value={dueDate}
          onChange={v => setField('dueDate', v)}
          error={errors.dueDate}
        />

        {/* J7-W5 task 6c-1: Parent Work Item (optional, sub-item picker) */}
        {(parentCandidates !== undefined || defaultParentWorkItemId) && (
          <div>
            <Select
              label={t('Parent work item')}
              options={[
                { value: 'none', label: t('Pick a parent work item…') },
                ...((parentCandidates ?? []).map((c) => ({ value: c.id, label: c.title }))),
              ]}
              value={parentWorkItemId || 'none'}
              onValueChange={(v) => setField('parentWorkItemId', v === 'none' ? '' : v)}
              disabled={!!defaultParentWorkItemId}
              hint={
                defaultParentWorkItemId
                  ? t('A work item can only be one level deep (no grandchildren in this version).')
                  : t('Optional. Make this a sub-item under an existing work item.')
              }
              data-testid="parent-work-item-select"
            />
          </div>
        )}

        {/* Parent Routine (optional — J3-fix-wave-4 BUG-2) */}
        <div>
          <Select
            label={t('Parent Routine')}
            options={routineOptions}
            value={parentRoutineId || 'none'}
            onValueChange={(v) => setField('parentRoutineId', v === 'none' ? '' : v)}
            disabled={loadingRoutines || !domainId || domainId === 'none'}
            hint={
              !domainId || domainId === 'none'
                ? t('Select a domain first')
                : loadingRoutines
                ? t('Loading routines…')
                : routines.length === 0
                ? t('Optional — no routines in this domain yet. You can link one later.')
                : t('Optional — link to a parent routine if one exists for this domain.')
            }
          />
        </div>

        {/* Definition of Done (optional) */}
        <div>
          <Textarea
            label={t('Definition of Done')}
            value={definitionOfDone}
            onChange={e => setField('definitionOfDone', e.target.value)}
            placeholder={t('What evidence will prove this work item is complete?')}
            rows={2}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t('Optional. See')}{' '}
            <a
              href="/roros/artifacts/knowledge"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-primary hover:text-primary/80"
            >
              {t('Verification Policy')}
            </a>
            {' '}{t('for evidence standards.')}
          </p>
        </div>
      </div>
    </FormSheet>
  );
}
